import {Button, FormControl, FormField, IconButton, TableRow} from 'platform/components';
import {Box, Show, Text} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {BalloonFinancingCellFormValues} from './schemas/balloonFinancingCellFormSchema';
import {BalloonVariantDraft} from './types/BalloonVariantDraft';
import {VehicleFinancingPricing} from './types/VehicleFinancingPricing';
import {calculateFinancingOffer, FinancingInsurance} from './utils/calculateFinancingOffer';
import {isConfiguredBalloonVariant} from './utils/financingCells/isConfiguredBalloonVariant';

interface BalloonVariantRowProps {
  control: FormControl<BalloonFinancingCellFormValues>;
  index: number;
  variant: BalloonVariantDraft;
  term: number;
  downPayment: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  insurance: FinancingInsurance;
  setActiveVariantLabel?: string;
  isReadOnly: boolean;
  isPreviewed: boolean;
  isActive: boolean;
  onPreview: () => void;
  onSetActive: () => void;
  onRateChange: () => void;
  onClear: () => void;
}

/** One editable balloon variant of the balloon financing dialog's variant table. */
export function BalloonVariantRow(props: BalloonVariantRowProps) {
  const formatCurrency = useFormatCurrency();
  const variantCalculation =
    props.variant.nominalRate === null
      ? null
      : calculateFinancingOffer({
          pricing: props.pricing,
          term: props.term,
          downPayment: props.downPayment,
          balloonPayment: props.variant.balloonPayment,
          nominalRate: props.variant.nominalRate,
          insurance: props.insurance,
        });
  const activeActionLabel =
    props.setActiveVariantLabel ?? i18n.t('page.vehicle.financingAndLeasing.setAsActiveVariant');
  const activeLabel = `${activeActionLabel} · ${props.variant.balloonPayment} %`;

  return (
    <TableRow
      color={props.isPreviewed ? 'palettes.yellow.20.100' : undefined}
      hoverColor={props.isPreviewed ? 'palettes.yellow.30.100' : 'palettes.neutral.20.100'}
      onRowClick={props.onPreview}
    >
      <Box paddingHorizontal={1} paddingVertical={2}>
        <IconButton
          icon="toggle/star"
          size="small"
          severity={props.isActive ? 'warning' : undefined}
          isDisabled={
            props.isReadOnly || !isConfiguredBalloonVariant(props.variant) || props.isActive
          }
          aria-label={activeLabel}
          onClick={(event) => {
            event.stopPropagation();
            props.onSetActive();
          }}
        />
      </Box>
      <Box paddingHorizontal={1} paddingVertical={2}>
        <Button
          title={`${props.variant.balloonPayment} %`}
          variant="ghostLink"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            props.onPreview();
          }}
        />
      </Box>
      <Box paddingHorizontal={1} paddingVertical={2}>
        <FormField
          control={props.control}
          name={`variants.${props.index}.nominalRate`}
          type="number"
          label={null}
          aria-label={`${i18n.t(
            'page.vehicle.financingAndLeasing.nominalRate'
          )} · ${props.variant.balloonPayment} %`}
          suffix="%"
          decimalPlaces={2}
          minStepperValue={0}
          isDisabled={props.isReadOnly}
          data-testid={`balloon-${props.variant.balloonPayment}-nominal-rate`}
          onChange={props.onRateChange}
        />
      </Box>
      <Box paddingHorizontal={1} paddingVertical={2}>
        <FormField
          control={props.control}
          name={`variants.${props.index}.apr`}
          type="number"
          label={null}
          aria-label={`${i18n.t('page.vehicle.financingAndLeasing.apr')} · ${
            props.variant.balloonPayment
          } %`}
          suffix="%"
          decimalPlaces={2}
          minStepperValue={0}
          isDisabled={props.isReadOnly}
          data-testid={`balloon-${props.variant.balloonPayment}-apr`}
          onChange={props.onRateChange}
        />
      </Box>
      <Box paddingHorizontal={1} paddingVertical={2}>
        <Text
          size="small"
          color={isNotNil(variantCalculation) ? 'link' : 'secondary'}
          alternative={isNotNil(variantCalculation) ? true : undefined}
        >
          {isNotNil(variantCalculation)
            ? formatCurrency(variantCalculation.monthlyRate, props.currency)
            : '—'}
        </Text>
      </Box>
      <Box paddingHorizontal={1} paddingVertical={2}>
        <Show when={props.variant.nominalRate !== null || props.variant.apr !== null}>
          <IconButton
            icon="navigation/close"
            size="small"
            isDisabled={props.isReadOnly}
            aria-label={`${i18n.t('general.actions.clear')} · ${props.variant.balloonPayment} %`}
            onClick={(event) => {
              event.stopPropagation();
              props.onClear();
            }}
          />
        </Show>
      </Box>
    </TableRow>
  );
}
